import React, { useMemo } from "react";

import { clsx } from "clsx";

import { FilterProvider } from "~/providers/FilterProvider";
import { NostoProvider } from "~/providers/NostoProvider";

import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

import { FiltersDesktop } from "~/components/Filter/FiltersDesktop";
import { SearchResults } from "~/components/Search/Results/SearchResults";
import { Category } from "~/components/Nosto/Category";
import { Filters } from "~/components/Nosto/Filters";
import { Sections } from "~/components/Sections/Sections";
import {
  BackgroundContainer,
  StyledContainer,
} from "~/components/Styled/Container";

import { CollectionHeader } from "./CollectionHeader";

const HeaderContainer = (props: any) => (
  <StyledContainer
    {...props}
    className={clsx(
      props.className,
      "mx-auto max-w-full !px-zero pb-maxi pt-maxi",
    )}
  />
);

const FooterContainer = (props: any) => (
  <StyledContainer
    {...props}
    className={clsx(props.className, "mx-auto max-w-full !px-zero pb-maxi")}
  />
);

export interface CollectionProps {
  content: any;
  title: string;
  handle: string;
  footerContent: any;
  descriptionHtml: string;
  primaryCollection: any;
  hideSizeFilter: boolean;
  collectionGender: string | null;
}

export function Collection({
  title,
  handle,
  content,
  footerContent,
  hideSizeFilter,
  descriptionHtml,
  collectionGender,
  primaryCollection,
}: CollectionProps) {
  const { enableNosto } = useRouteLoaderData("routes/$site");

  const hideBreadcrumbAndTitle: boolean =
    content?.find((item) => item._type === "sectionCollectionHeroBanner")
      ?.hideBreadcrumbAndTitle || false;

  const collectionHeroBannerContent = content?.find(
    (item) => item._type === "sectionCollectionHeroBanner",
  );

  const paths = useMemo(() => {
    if (hideBreadcrumbAndTitle) {
      return [];
    }

    const genderLink = {
      title:
        collectionGender && collectionGender != "Accessories"
          ? collectionGender
          : "Home",
      url:
        collectionGender && collectionGender != "Accessories"
          ? `/collections/${collectionGender.includes("Women") ? "all-womens" : "all-mens"}`
          : "/",
    };

    const primaryCollectionLink = primaryCollection?.reference
      ? {
          title: primaryCollection.reference.title.replace(
            /(Women's|Men's)\s*/g,
            "",
          ),
          url: `/collections/${primaryCollection.reference.handle}`,
        }
      : null;

    const pathArray = [genderLink];
    if (primaryCollectionLink) pathArray.push(primaryCollectionLink);

    // Add current collection as final item without URL
    pathArray.push({
      title: title,
    });

    return pathArray;
  }, [collectionGender, primaryCollection, hideBreadcrumbAndTitle, title]);

  return (
    <>
      {enableNosto ? (
        <NostoProvider collectionTitle={title} gender={collectionGender || ""}>
          <BackgroundContainer background="light">
            {!collectionHeroBannerContent &&
            content &&
            Array.isArray(content) &&
            content.length > 0 ? (
              <HeaderContainer data-testid="collection-page-header">
                <Sections page={{ content }} />
              </HeaderContainer>
            ) : null}
            <CollectionHeader
              paths={paths}
              title={title}
              description={descriptionHtml}
              hideBreadcrumbAndTitle={hideBreadcrumbAndTitle}
              collectionHeroBannerContent={collectionHeroBannerContent}
            />

            <div className="flex flex-col border-t border-primary-300">
              <Filters />
              <Category />
            </div>

            {footerContent &&
            Array.isArray(footerContent) &&
            footerContent.length > 0 ? (
              <FooterContainer data-testid="collection-page-footer">
                <Sections page={{ content: footerContent }} />
              </FooterContainer>
            ) : null}
          </BackgroundContainer>
        </NostoProvider>
      ) : (
        <FilterProvider
          gender={collectionGender || ""}
          collectionTitle={title}
          hideSizeFilter={!!hideSizeFilter}
        >
          <BackgroundContainer background="light">
            {!collectionHeroBannerContent &&
            content &&
            Array.isArray(content) &&
            content.length > 0 ? (
              <HeaderContainer data-testid="collection-page-header">
                <Sections page={{ content }} />
              </HeaderContainer>
            ) : null}
            <CollectionHeader
              paths={paths}
              title={title}
              description={descriptionHtml}
              hideBreadcrumbAndTitle={hideBreadcrumbAndTitle}
              collectionHeroBannerContent={collectionHeroBannerContent}
            />
            <div className="flex flex-col border-t border-primary-300">
              <FiltersDesktop />
              <SearchResults collection={{ handle, title }} />
            </div>
            {footerContent &&
            Array.isArray(footerContent) &&
            footerContent.length > 0 ? (
              <FooterContainer data-testid="collection-page-footer">
                <Sections page={{ content: footerContent }} />
              </FooterContainer>
            ) : null}
          </BackgroundContainer>
        </FilterProvider>
      )}
    </>
  );
}
